import HourGlassIcon from "@shared/svg/hour-glass.svg?react";
import { Avatar } from "@shared/ui/Avatar";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import Label, { LABEL_SIZES } from "@shared/ui/Label";
import moment from "moment-timezone";
import React from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { ListItemBoxed } from "~/components/_listItem";

const EventName = ({
  eventName,
  machineName,
  machineFacilityName,
  machineFacilityId,
  machineId,
}: {
  eventName: string;
  machineName: string;
  machineFacilityName: string;
  machineId: string;
  machineFacilityId: string;
}) => {
  const navigate = useNavigate();

  return (
    <div className="flex flex-1 basis-2/4 min-w-[160px]">
      <span className="flex shrink-0 items-center justify-center h-5xl w-5xl rounded-full bg-mint-lightest mr-lg">
        <HourGlassIcon
          width={20}
          height={20}
          className="text-mintBlue stroke-mintBlue fill-mintBlue ml-xs"
        />
      </span>
      <div className="flex flex-col min-w-0 overflow-hidden pr-sm">
        <Headline
          size={HEADLINE_SIZES.X_SMALL}
          className="overflow-hidden text-ellipsis whitespace-nowrap capitalize"
          title={eventName}
        >
          {eventName}
        </Headline>
        <div className="flex items-center min-w-0">
          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            color={machineFacilityId ? "text-brand" : ""}
            className={`truncate mt-xs min-w-0 max-w-[40%] ${
              machineFacilityId ? "hover:cursor-pointer" : ""
            }`}
            title={machineFacilityName}
            {...(machineFacilityId
              ? {
                  onClick: () =>
                    navigate(`/app/facilities/${machineFacilityId}`),
                }
              : {})}
          >
            {machineFacilityName}
          </BodyText>
          <span className="shrink-0 leading-none h-3">&nbsp; • &nbsp;</span>
          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            color="text-brand"
            className="truncate mt-xs min-w-0 hover:cursor-pointer"
            title={machineName}
            onClick={() => navigate(`/app/assets/all/${machineId}`)}
          >
            {machineName}
          </BodyText>
        </div>
      </div>
    </div>
  );
};

const EvenDetail = ({
  eventId,
  label,
  isDate = true,
  dateFormat,
}: {
  eventId: string;
  label: string;
  isDate: boolean;
  dateFormat: string;
}) => (
  <div className="flex shrink-0 items-center overflow-hidden whitespace-nowrap">
    <Label
      size={LABEL_SIZES.SMALL}
      color="text-secondary"
      className="shrink-0 font-medium"
    >
      {label}
      <>: &nbsp; </>
    </Label>
    <BodyText
      size={BODY_TEXT_SIZES.X_SMALL}
      className="truncate"
      title={eventId}
    >
      {isDate ? (
        <span className="date">{moment(eventId).format(dateFormat)}</span>
      ) : (
        eventId
      )}
    </BodyText>
  </div>
);

const EventCreator = ({ creator }: { creator: { name: string } }) => {
  const { messages } = useIntl();
  return (
    <div className="shrink-0 w-32 md:w-44 lg:w-52 min-w-0 overflow-hidden">
      <Avatar
        name={creator?.name || messages?.common?.["noAssignee"]}
        showName
        hasNoUser={!creator?.name}
      />
    </div>
  );
};

const EventTile = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className: string;
}) => <ListItemBoxed className={className}>{children}</ListItemBoxed>;

export default Object.assign(EventTile, {
  EvenDetail,
  EventCreator,
  EventName,
});
